export const DEFAULT_FHIR_BIRTH_DATE = '1800-01-01';

export function isDefaultDateOfBirth(value) {
  if (!value) return false;

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '0') return false;
  if (trimmed.startsWith(DEFAULT_FHIR_BIRTH_DATE)) return true;
  if (trimmed.replace(/\D/g, '') === '01011800') return true;

  if (/^\d+$/.test(trimmed)) {
    const date = new Date(Number(trimmed));
    if (!Number.isNaN(date.getTime())) {
      const local = date.getFullYear() === 1800 && date.getMonth() === 0 && date.getDate() === 1;
      const utc = date.getUTCFullYear() === 1800 && date.getUTCMonth() === 0 && date.getUTCDate() === 1;
      return local || utc;
    }
  }

  return false;
}

export function resolveBirthInput({ birthDate, age } = {}) {
  const raw = String(age || '').trim();
  if (raw.startsWith('~')) {
    const parsed = Number(raw.replace(/^~/, '').trim());
    if (!Number.isNaN(parsed)) {
      return {
        birthInputMode: 'estimatedAge',
        birth_date: '',
        estimated_age: String(Math.trunc(parsed)),
      };
    }
  }

  return {
    birthInputMode: 'dateOfBirth',
    birth_date: isDefaultDateOfBirth(birthDate) ? '' : String(birthDate || '').trim(),
    estimated_age: '',
  };
}

export function switchBirthInputMode(formData, nextMode) {
  if (!nextMode || nextMode === formData.birthInputMode) return formData;

  const toEstimated = nextMode === 'estimatedAge';

  return {
    ...formData,
    birthInputMode: nextMode,
    cached_birth_date: toEstimated ? formData.birth_date : formData.cached_birth_date,
    cached_estimated_age: toEstimated ? formData.cached_estimated_age : formData.estimated_age,
    birth_date: toEstimated ? '' : formData.cached_birth_date || '',
    estimated_age: toEstimated ? formData.cached_estimated_age || '' : '',
  };
}

export function parseIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || '').trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }

  return date;
}

export function getBirthInputErrors({
  birthInputMode,
  birthDate,
  estimatedAge,
  required = true,
} = {}) {
  if (birthInputMode === 'estimatedAge') {
    if (estimatedAge === '' || estimatedAge == null) {
      return { birth_date: '', estimated_age: required ? 'field-required' : '' };
    }

    const parsed = Number(String(estimatedAge).trim());
    if (!Number.isInteger(parsed)) return { birth_date: '', estimated_age: 'invalid-estimated-age' };
    if (parsed < 0) return { birth_date: '', estimated_age: 'min-estimated-age' };
    if (parsed > 150) return { birth_date: '', estimated_age: 'max-estimated-age' };

    return { birth_date: '', estimated_age: '' };
  }

  const trimmed = String(birthDate || '').trim();
  if (!trimmed) return { birth_date: required ? 'field-required' : '', estimated_age: '' };
  if (isDefaultDateOfBirth(trimmed)) return { birth_date: 'invalid-date-format', estimated_age: '' };

  const date = parseIsoDate(trimmed);
  if (!date) return { birth_date: 'invalid-date-format', estimated_age: '' };

  const start = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (date < start(new Date(1901, 0, 1))) return { birth_date: 'min-date-of-birth', estimated_age: '' };
  if (date > start(new Date())) return { birth_date: 'max-date-of-birth', estimated_age: '' };

  return { birth_date: '', estimated_age: '' };
}

export function buildPatientAgePayload({
  birthInputMode,
  birthDate,
  estimatedAge,
  startTime,
} = {}) {
  if (birthInputMode === 'estimatedAge') {
    const parsed = Number(estimatedAge);
    if (estimatedAge === '' || estimatedAge == null || Number.isNaN(parsed)) return null;
    return `~${parsed}`;
  }

  const birth = parseIsoDate(birthDate);
  if (!birth) return null;

  const reference = startTime ? new Date(startTime) : new Date();
  const ref = Number.isNaN(reference.getTime()) ? new Date() : reference;
  let age = ref.getFullYear() - birth.getFullYear();
  const month = ref.getMonth() - birth.getMonth();
  if (month < 0 || (month === 0 && ref.getDate() < birth.getDate())) age -= 1;

  return age;
}
